export const ISO8601_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;

const MONTH_ABBREV = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Formats a date as "MMM D, HH:MM UTC" (e.g. "Apr 18, 14:32 UTC").
 */
export function formatUtcDateTime(date: Date): string {
  const month = MONTH_ABBREV[date.getUTCMonth()];
  const day = date.getUTCDate();
  const hours = pad2(date.getUTCHours());
  const minutes = pad2(date.getUTCMinutes());
  return `${month} ${day}, ${hours}:${minutes} UTC`;
}

/**
 * Short UTC timestamp for chat-style UIs. Always includes the time; date is
 * prepended for anything outside today:
 *   - same UTC day as `now` → "HH:MM UTC" (e.g. "14:32 UTC")
 *   - earlier this UTC year  → "MMM D, HH:MM UTC" (e.g. "Apr 18, 14:32 UTC")
 *   - prior UTC year         → "MMM D, YYYY, HH:MM UTC" (e.g. "Apr 18, 2025, 14:32 UTC")
 */
export function formatUtcRelativeShort(
  date: Date,
  now: Date = new Date()
): string {
  const hours = pad2(date.getUTCHours());
  const minutes = pad2(date.getUTCMinutes());
  const sameDay =
    date.getUTCFullYear() === now.getUTCFullYear() &&
    date.getUTCMonth() === now.getUTCMonth() &&
    date.getUTCDate() === now.getUTCDate();

  if (sameDay) {
    return `${hours}:${minutes} UTC`;
  }

  const month = MONTH_ABBREV[date.getUTCMonth()];
  const day = date.getUTCDate();
  if (date.getUTCFullYear() === now.getUTCFullYear()) {
    return `${month} ${day}, ${hours}:${minutes} UTC`;
  }
  return `${month} ${day}, ${date.getUTCFullYear()}, ${hours}:${minutes} UTC`;
}

/**
 * Short timestamp for chat-style UIs in the viewer's local time zone, using
 * 12-hour time with AM/PM:
 *   - same local day as `now` → "h:MM AM/PM" (e.g. "2:32 PM")
 *   - earlier this local year → "MMM D, h:MM AM/PM" (e.g. "Apr 18, 2:32 PM")
 *   - prior local year        → "MMM D, YYYY, h:MM AM/PM"
 */
export function formatLocalRelativeShort(
  date: Date,
  now: Date = new Date()
): string {
  const rawHours = date.getHours();
  const hours = ((rawHours + 11) % 12) + 1;
  const minutes = pad2(date.getMinutes());
  const meridiem = rawHours < 12 ? 'AM' : 'PM';
  const time = `${hours}:${minutes} ${meridiem}`;

  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) {
    return time;
  }

  const month = MONTH_ABBREV[date.getMonth()];
  const day = date.getDate();
  if (date.getFullYear() === now.getFullYear()) {
    return `${month} ${day}, ${time}`;
  }
  return `${month} ${day}, ${date.getFullYear()}, ${time}`;
}
